import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';

import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { Button } from '@mui/material';
import Divider from '@mui/material/Divider';
import Toolbar from '@mui/material/Toolbar';
import MuiDrawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import CssBaseline from '@mui/material/CssBaseline';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import TodoPage from './pages/TodoPage';
import StartupPage from './pages/StartupPage';
import ChangeLogs from './pages/ALIT/ChangeLogs';
import TrackingWrapper from './pages/Xero/Tracking';
import FeatureFlags from './pages/Other/FeatureFlags';
import EditAdminUser from './pages/Other/EditAdminUser';
import BackupViewer from './pages/IccGeneral/ViewBackup';
import EditIccUser from './pages/IccGeneral/EditIccUser';
import BillingStatus from './pages/Billing/BillingStatus';
import ViewAdminUsers from './pages/Other/ViewAdminUsers';
import PricingProducts from './pages/ALIT/PricingProducts';
import ViewIccUsers from './pages/IccGeneral/ViewIccUsers';
import CreateAdminUser from './pages/Other/CreateAdminUser';
import SetupCommission from './pages/Other/SetupCommission';
import QuotesViewerWrapper from './pages/Xero/QuotesViewer';
import UpdateBoothId from './pages/IccGeneral/UpdateBoothId';
import CreateIccUser from './pages/IccGeneral/CreateIccUser';
import EditIccBranch from './pages/IccGeneral/EditIccBranch';
import ViewReportUser from './pages/IccGeneral/ViewReportUser';
import EditReportUser from './pages/IccGeneral/EditReportUser';
import CreateDatabase from './pages/IccGeneral/CreateDatabase';
import EditMobileConfig from './pages/Mobile/EditMobileConfig';
import UpdateMaskImages from './pages/Mobile/UpdateMaskImages';
import CreateIccBranch from './pages/IccGeneral/CreateIccBranch';
import EditMaskStructure from './pages/Mobile/EditMaskStructure';
import CreateReportUser from './pages/IccGeneral/CreateReportUser';
import WorkflowNewCustomer from './pages/Other/WorkflowNewCustomer';
import EditBillingUserWrapper from './pages/Billing/EditBillingUser';
import CreateMaskStructure from './pages/Mobile/CreateMaskStructure';
import RecoverAdminPassword from './pages/Other/RecoverAdminPassword';
import XeroInvoiceStatusWrapper from './pages/Xero/XeroInvoiceStatus';
import MonthlyUsageReport from './pages/IccGeneral/MonthlyUsageReport';
import CreateXeroInvoicesWrapper from './pages/Xero/CreateXeroInvoices';
import ReportImageGrouping from './pages/IccGeneral/ReportImageGrouping';
import CreateBillingUserWrapper from './pages/Billing/CreateBillingUser';
import EditBillingProductWrapper from './pages/Billing/EditBillingProduct';
import CreateBillingProductWrapper from './pages/Billing/CreateBillingProduct';

import { api } from '../../utils';
import { IccUser } from '../../models/AdminPageModels';
import { IError } from '../../models/ReportPageModels';
import ModuleNameAndFavourite from './controls/ModuleNameAndFavourite';
import { AdminUser } from './pages/Other/ViewAdminUsers/ViewAdminUsers';
import { ReportUserDisplayModel } from './pages/IccGeneral/ViewReportUser/ViewReportUser';
import { AdminMenu, AdminMenus, MenuCategories, MenuHandlers } from '../../utils/AdminMenus';

const drawerWidth = 250;

const StartupModuleName: string = "Admin Dashboard";

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 10px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

interface CheckAccessRequest {
    licence: string;
}
interface CheckAccessResponse {
    data: CheckAccessData;
    error: IError;
}
interface CheckAccessData {
    alitSystems: number;
    iccGeneral: number;
    mobile: number;
    billing: number;
    xero: number;
    other: number;
}


function Admin() {
    const { enqueueSnackbar } = useSnackbar();

    const navigate = useNavigate();

    const theme = useTheme();
    const [open, setOpen] = React.useState(false);

    const [barText, setBarText] = React.useState<string>(StartupModuleName);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const [menus, setMenus] = useState<Map<string, AdminMenu>>(new Map());

    const [currentHandler, setCurrentHandler] = React.useState<number>(MenuHandlers.Startup);

    const userAccess = useRef<Map<String, boolean>>(new Map());

    useEffect(() => {
        CheckAccess();
    }, [])

    const CheckAccess = async () => {
        var body: CheckAccessRequest = {
            licence: localStorage.getItem("token") ?? ""
        };

        try {
            const response: CheckAccessResponse = await api.post("api/Authorization/CheckAccess", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                var access = response.data;
                var dict = userAccess.current;
                dict.set(MenuCategories.ALITSystems, access.alitSystems === 1);
                dict.set(MenuCategories.ICCGeneral, access.iccGeneral === 1);
                dict.set(MenuCategories.Mobile, access.mobile === 1);
                dict.set(MenuCategories.Billing, access.billing === 1);
                dict.set(MenuCategories.Xero, access.xero === 1);
                dict.set(MenuCategories.Other, access.other === 1);

                setMenus(AdminMenus);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
            captureException(err);
        }
    }

    function SelectModule(moduleName: string, handler: number) {
        setBarText(moduleName);
        setCurrentHandler(handler);
    }

    function OpenInNewTab(route: string) {
        window.open(`https://admindash.auto.works/${route}`, "_blank", "noreferrer");
    }

    function OpenOtherInNewTab(url: string) {
        window.open(url, "_blank", "noreferrer");
    }

    function Logout() {
        window.localStorage.removeItem("token");
        navigate('/login');
    }

    function handleReturnHome() {
        SelectModule(StartupModuleName, MenuHandlers.Startup);
    }

    function UserHasAccess(item: AdminMenu): boolean {
        return userAccess.current.get(item.Type)!;
    }

    // ICC users
    const [editIccUser, setEditIccUser] = React.useState<IccUser | null>(null);
    function EditIccUserFunc(user: IccUser) {
        setEditIccUser(user);
        SelectModule("Edit ICC user", MenuHandlers.EditIccUser);
    }
    // Admin Users
    const [editAdminUser, setEditAdminUser] = React.useState<AdminUser | null>(null);
    function EditAdminUserFunc(user: AdminUser) {
        setEditAdminUser(user);
        SelectModule("Edit Admin user", MenuHandlers.EditAdminUser);
    }
    // Report Users
    const [editReportUser, setEditReportUser] = React.useState<ReportUserDisplayModel | null>(null);
    function EditReportUserFunc(user: ReportUserDisplayModel) {
        setEditReportUser(user);
        SelectModule("Edit Report user", MenuHandlers.EditReportUser);
    }

    function RenderHandlers() {
        switch (currentHandler) {
            case MenuHandlers.Startup:
                return <StartupPage selectModule={SelectModule} />;

            case MenuHandlers.InsightlyCRM:
                OpenOtherInNewTab("https://login.insightly.com/User/Login");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.WebsiteLogin:
                OpenOtherInNewTab("https://www.autoloadit.com/wp-login.php");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Supportbee:
                OpenOtherInNewTab("https://autoloadit.supportbee.com/members/sign_in");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.KnowledgeBase:
                OpenOtherInNewTab("https://autoloadit.supportbee.com/kb_admin/sites/2420-autoload-it-ltd-help-and-support/categories");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.PasswordSender:
                OpenOtherInNewTab("https://onetimesecret.com/");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.ClientOnlineReport:
                OpenOtherInNewTab("https://report.auto.works/login");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.XeroAccounts:
                OpenOtherInNewTab("https://login.xero.com/identity/user/login");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Voice:
                OpenOtherInNewTab("https://voice.google.com/u/0/voicemail");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Purchases:
                OpenOtherInNewTab("https://docs.google.com/spreadsheets/d/1Rgbnqgaqpoiv505O0Itf6lRphML77aCdTixxzM_FICk/edit?usp=sharing");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Scheduler:
                OpenOtherInNewTab("https://docs.google.com/spreadsheets/d/1JPzMyqEQ6hHBgvfwtm1I4Nooo9QVz4ObPGiP0oEWuTE/edit?usp=sharing");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.ICC2Manual:
                OpenOtherInNewTab("https://docs.google.com/document/d/10dlMih7SfIcwl_EVNsSf0Ot8DIuUKRCVk0lNwxcDUVc/edit?usp=sharing");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Jira:
                OpenOtherInNewTab("https://existek.atlassian.net/jira/software/projects/AUT/boards/11");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Bitbucket:
                OpenOtherInNewTab("https://bitbucket.org/existek/workspace/projects/AUT");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Apple:
                OpenOtherInNewTab("https://idmsa.apple.com/IDMSWebAuth/signin?appIdKey=891bd3417a7776362562d2197f89480a8547b108fd934911bcbea0110d07f757&path=%2Faccount%2F&rv=1#/overview/RB3B5Y775B");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.AppCenter:
                OpenOtherInNewTab("https://appcenter.ms/orgs/Existek/applications");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.PricingALITProducts:
                return <PricingProducts />
            case MenuHandlers.EquipmentStockList:
                OpenOtherInNewTab("https://docs.google.com/spreadsheets/d/13hjaVLZHGFaH5MHD11ayLmMgyqdnP4TN8nFj07ltbYA/edit?usp=sharing");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.QuotesViewer:
                return <QuotesViewerWrapper />
            case MenuHandlers.QuotePricingDrawing:
                OpenOtherInNewTab("https://docs.google.com/spreadsheets/d/1BXZQEIFaE1Y8GcbUrO5r1jQ4VqCo25PlOVH3aEHu6zU");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.ChangeLogs:
                return <ChangeLogs />

            case MenuHandlers.ViewIccUsers:
                return <ViewIccUsers editUser={EditIccUserFunc} />
            case MenuHandlers.EditIccUser:
                return <EditIccUser editUser={editIccUser} />
            case MenuHandlers.CreateIccUser:
                return <CreateIccUser />
            case MenuHandlers.CreateIccBranch:
                return <CreateIccBranch />
            case MenuHandlers.EditIccBranch:
                return <EditIccBranch />
            case MenuHandlers.CreateDatabase:
                return <CreateDatabase />
            case MenuHandlers.UpdateBoothId:
                return <UpdateBoothId />
            case MenuHandlers.ViewReportUser:
                return <ViewReportUser editUser={EditReportUserFunc} />
            case MenuHandlers.EditReportUser:
                return <EditReportUser editUser={editReportUser} />
            case MenuHandlers.CreateReportUser:
                return <CreateReportUser />
            case MenuHandlers.WorkflowNewCustomer:
                return <WorkflowNewCustomer />
            case MenuHandlers.ReportImageGrouping:
                return <ReportImageGrouping />
            case MenuHandlers.MonthlyUsageReport:
                return <MonthlyUsageReport />
            case MenuHandlers.BackupViewer:
                return <BackupViewer />


            case MenuHandlers.IosTechManual:
                OpenOtherInNewTab("https://docs.google.com/document/d/1lj9U3X4G-UVDqKViS3GLjdclMxYavsXN6NvS6wAJ4yA");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.EditMobileConfig:
                return <EditMobileConfig />
            case MenuHandlers.CreateMaskStructure:
                return <CreateMaskStructure />
            case MenuHandlers.EditMaskStructure:
                return <EditMaskStructure />
            case MenuHandlers.UpdateMaskImages:
                return <UpdateMaskImages />

            case MenuHandlers.BillingInstructionManual:
                OpenOtherInNewTab("https://docs.google.com/document/d/1H-xSqzNWcp65wOxDzXCM1iLceDTLES7lvzmGcq_eImY/edit?usp=sharing");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.CreateBilling:
                OpenInNewTab("billing");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.BillingSettings:
                OpenInNewTab("billing/settings");
                setCurrentHandler(MenuHandlers.Startup)
                break;

            case MenuHandlers.EditBillingUser:
                return <EditBillingUserWrapper />
            case MenuHandlers.CreateBillingUser:
                return <CreateBillingUserWrapper />
            case MenuHandlers.EditBillingProduct:
                return <EditBillingProductWrapper />
            case MenuHandlers.CreateBillingProduct:
                return <CreateBillingProductWrapper />
            case MenuHandlers.BillingStatus:
                return <BillingStatus />
            case MenuHandlers.BillingSearch:
                return <TodoPage />


            case MenuHandlers.CreateXeroInvoices:
                return <CreateXeroInvoicesWrapper />
            case MenuHandlers.XeroTracking:
                return <TrackingWrapper />
            case MenuHandlers.XeroInvoiceStatus:
                return <XeroInvoiceStatusWrapper />


            case MenuHandlers.Commission:
                OpenInNewTab("commission");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.SetupCommission:
                return <SetupCommission />
            case MenuHandlers.CommissionPayRoll:
                OpenInNewTab("commission/payroll");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.Deduplication:
                OpenInNewTab("deduplication");
                setCurrentHandler(MenuHandlers.Startup)
                break;
            case MenuHandlers.ViewAdminUsers:
                return <ViewAdminUsers editUser={EditAdminUserFunc} />
            case MenuHandlers.EditAdminUser:
                return <EditAdminUser editUser={editAdminUser} />
            case MenuHandlers.CreateAdminUser:
                return <CreateAdminUser />
            case MenuHandlers.RecoverAdminPassword:
                return <RecoverAdminPassword />
            case MenuHandlers.FeatureFlags:
                return <FeatureFlags />

            default:
                return <TodoPage />
        }
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: 5,
                            ...(open && { display: 'none' }),
                        }}>
                        <MenuIcon />
                    </IconButton>

                    <ModuleNameAndFavourite moduleName={barText} />

                    <div style={{ right: 0, position: "absolute", marginRight: "10px" }}>
                        <IconButton onClick={handleReturnHome}>
                            <HomeIcon sx={{ color: "white", fontSize: "26px" }} />
                        </IconButton>

                        <Button
                            sx={{ color: "white", fontSize: "20px", fontWeight: "bolder" }}
                            size="large"
                            variant="text"
                            onClick={Logout}>
                            Logout
                        </Button>
                    </div>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open}>
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List sx={{ overflowY: "auto", overflowX: "hidden" }}>
                    {Array.from(menus.entries()).map(([menuName, item]: [string, AdminMenu]) => {
                        if (!UserHasAccess(item))
                            return null;

                        if (item.Handler === 0)
                            return <div key={menuName}>
                                <Divider />
                                <p> {menuName}</p>
                            </div>

                        return <ListItem
                            key={menuName}
                            disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5, }}
                                onClick={() => SelectModule(menuName, item.Handler)}>
                                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}>
                                    {item.Icon}
                                </ListItemIcon>
                                <ListItemText primary={menuName} sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>
                    })}
                </List>
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <DrawerHeader />

                {RenderHandlers()}
            </Box>
        </Box>
    )
}

export default Admin;